import { useTheme } from '@mui/material';
import { useResizeObserver } from '@procureai/uix';
import { flatten, toPairs } from 'ramda';
import { useCallback, useState, useRef, useMemo, RefObject, ComponentProps } from 'react';
import {
  GeoJSONSource,
  GeolocateErrorEvent,
  GeolocateResultEvent,
  Map as ReactMapGl,
  MapLayerMouseEvent,
  GeolocateControlProps,
  MapRef,
  LayerProps,
} from 'react-map-gl';

import { LatLng } from '~/common/types';

import { MapLayerId, MapPopupInfo, MapSourceId, MapMarkerVariant, MapViewState } from './Map.types';
import { addMarkersToMap } from './Map.util';

type ReactMapGlProps = ComponentProps<typeof ReactMapGl>;

type LayersProps = LayerProps & {
  groupId?: MapSourceId;
};

export interface UseMapOptions {
  viewState?: MapViewState;
  onSelect?: (id: string) => void;
  onViewStateSelect: (viewState: MapViewState) => void;
  markerVariants?: { [type: string]: MapMarkerVariant };
}

export interface UseMapResult {
  layers: LayersProps[];
  interactiveLayerIds: string[];
  userLocation: LatLng | null;
  rootRef: RefObject<HTMLDivElement | null>;
  mapRef: RefObject<MapRef | null>;
  popup: MapPopupInfo | null;
  onClick: ReactMapGlProps['onClick'];
  onLoad: ReactMapGlProps['onLoad'];
  onMouseEnter: ReactMapGlProps['onMouseEnter'];
  onMouseLeave: ReactMapGlProps['onMouseLeave'];
  onGeolocate: GeolocateControlProps['onGeolocate'];
  onGeolocateError: GeolocateControlProps['onError'];
}

const interactiveLayerIds: string[] = [MapLayerId.supplierGeoUnClusteredPoint];

export const useMap = ({ viewState, onSelect, markerVariants }: UseMapOptions): UseMapResult => {
  const rootRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapRef>(null);
  const theme = useTheme();

  const [popup, setPopup] = useState<MapPopupInfo | null>(null);

  useResizeObserver({
    ref: rootRef,
    onResize: () => {
      const map = mapRef.current?.getMap();
      if (map) {
        map.resize();
      }
    },
  });

  const [userLocation, setUserLocation] = useState<LatLng | null>(
    viewState
      ? {
          lat: viewState.latitude,
          lng: viewState.longitude,
        }
      : null,
  );

  const layers = useMemo(() => {
    return [
      {
        id: MapLayerId.supplierGeoClusters,
        groupId: MapSourceId.supplierGeoData,
        type: 'circle',
        filter: ['has', 'point_count'],
        paint: {
          'circle-stroke-width': 3,
          'circle-stroke-color': theme.palette.text.secondary,
          'circle-color': theme.palette.common.white,
          'circle-radius': ['step', ['get', 'point_count'], 14, 100, 18],
        },
      },
      {
        id: MapLayerId.supplierGeoClusterCount,
        groupId: MapSourceId.supplierGeoData,
        type: 'symbol',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          // 'text-field': '{point_count_abbreviated} ({potentialSuppliers} - {provenSuppliers})',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      },
      {
        id: MapLayerId.supplierGeoUnClusteredPoint,
        groupId: MapSourceId.supplierGeoData,
        type: 'symbol',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': markerVariants
            ? [
                'match',
                ['string', ['get', 'relationship']],
                ...flatten(toPairs(markerVariants)),
                MapMarkerVariant.DEFAULT,
              ]
            : MapMarkerVariant.DEFAULT,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
        },
      },
      {
        id: MapLayerId.locationGroupFill,
        groupId: MapSourceId.locationGroupData,
        type: 'fill',
        layout: {},
        paint: {
          'fill-color': theme.palette.chartPrimary[600],
          'fill-opacity': 0.1,
        },
      },
      {
        id: MapLayerId.locationGroupOutline,
        groupId: MapSourceId.locationGroupData,
        type: 'line',
        layout: {},
        paint: {
          'line-color': theme.palette.grey[900],
          'line-width': 0.75,
          'line-dasharray': [10, 20],
        },
      },
      {
        id: MapLayerId.locationGroupName,
        groupId: MapSourceId.locationGroupData,
        type: 'symbol',
        layout: {
          'text-field': '{name}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 10,
        },
      },
    ] as LayersProps[];
  }, [theme, markerVariants]);

  const onClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (mapRef.current && event.features && event.features[0]) {
        const feature = event.features[0];

        if (feature.layer?.id === MapLayerId.supplierGeoClusters) {
          const clusterId = feature.properties?.cluster_id;
          const mapboxSource = mapRef.current.getSource(MapSourceId.supplierGeoData) as GeoJSONSource;
          mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) {
              return;
            }
            if (mapRef.current) {
              mapRef.current.easeTo({
                center: (feature.geometry as any).coordinates,
                zoom: zoom ?? undefined,
                duration: 500,
              });
            }
          });
        } else if (feature.layer?.id === MapLayerId.supplierGeoUnClusteredPoint && onSelect) {
          if (feature.properties?.id) {
            onSelect(feature.properties?.id);
          }
        }
      }
    },
    [onSelect],
  );

  const onLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      addMarkersToMap(
        [
          { variant: MapMarkerVariant.PRIMARY, fill: theme.palette.secondary.main, fillOpacity: 1 },
          {
            variant: MapMarkerVariant.DEFAULT,
            fill: theme.palette.text.primary,
            fillOpacity: 1,
          },
        ],
        map,
      );
    }
  }, [theme]);

  const onGeolocate = useCallback((event: GeolocateResultEvent) => {
    setUserLocation({
      lat: event.coords.latitude,
      lng: event.coords.longitude,
    });
  }, []);

  const onGeolocateError = useCallback(
    (_event: GeolocateErrorEvent) => {
      if (mapRef.current && userLocation) {
        mapRef.current.easeTo({
          center: userLocation,
          zoom: viewState?.zoom,
          duration: 500,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userLocation],
  );

  const onMouseEnter = useCallback((event: MapLayerMouseEvent) => {
    event.target.getCanvas().style.cursor = 'pointer';

    if (event.features && event.features[0]) {
      const feature = event.features[0];

      if (interactiveLayerIds.includes(feature.layer?.id as MapLayerId)) {
        setPopup({
          lng: (feature.geometry as any).coordinates[0],
          lat: (feature.geometry as any).coordinates[1],
          id: feature.properties?.id,
          name: feature.properties?.name,
        });
      }
    }
  }, []);

  const onMouseLeave = useCallback((event: MapLayerMouseEvent) => {
    event.target.getCanvas().style.cursor = '';
    if (event.features && event.features[0]) {
      const feature = event.features[0];
      if (interactiveLayerIds.includes(feature.layer?.id as MapLayerId)) {
        setPopup(() => null);
      }
    }
  }, []);

  return {
    mapRef,
    rootRef,
    userLocation,
    onClick,
    onLoad,
    onMouseEnter,
    onMouseLeave,
    onGeolocate,
    onGeolocateError,
    popup,
    layers,
    interactiveLayerIds,
  };
};
